// Ricerca, filtri e ordinamento della lista clienti.
import type { Client, ClienteAttivita } from '../types';
import { getClientDisplayName, getClientEmail, getClientTelefono, getClientReferente, getClientPartitaIva, getClientPec, isClienteAzienda } from './clientDisplay';

export type FiltroTipoCliente = 'TUTTI' | 'PRIVATI' | 'AZIENDE';
export type FiltroAmbito = 'TUTTI' | 'INVESTIGATIVI' | 'FIDUCIARI';
export type TipoMandato = 'INVESTIGATIVO' | 'BONIFICA' | 'CONSULENZA';
export type FiltroPeriodo = 'TUTTO' | 'MESE' | 'TRE_MESI' | 'ANNO' | 'ANNO_SCORSO' | 'PERSONALIZZATO';
export type FiltroStato = 'TUTTI' | 'APERTI' | 'CONCLUSI' | 'SENZA';
// Alfabetico per nome mostrato, per cognome (privati) o per attività più recente.
export type OrdineClienti = 'NOME' | 'COGNOME' | 'RECENTI';

export interface FiltriClienti {
    cerca: string;
    tipo: FiltroTipoCliente;
    ambito: FiltroAmbito;
    tipiMandato: TipoMandato[];
    periodo: FiltroPeriodo;
    // Mesi 'YYYY-MM' del periodo personalizzato; vuoto = senza limite da quel lato.
    dal: string;
    al: string;
    stato: FiltroStato;
    ordine: OrdineClienti;
}

export const FILTRI_CLIENTI_VUOTI: FiltriClienti = {
    cerca: '', tipo: 'TUTTI', ambito: 'TUTTI', tipiMandato: [], periodo: 'TUTTO',
    dal: '', al: '', stato: 'TUTTI', ordine: 'NOME',
};

export const TIPI_MANDATO: [TipoMandato, string][] = [
    ['INVESTIGATIVO', 'Investigativo'], ['BONIFICA', 'Bonifica'], ['CONSULENZA', 'Consulenza'],
];

export const PERIODI: [FiltroPeriodo, string][] = [
    ['TUTTO', 'Qualsiasi periodo'], ['MESE', 'Questo mese'], ['TRE_MESI', 'Ultimi 3 mesi'],
    ['ANNO', "Quest'anno"], ['ANNO_SCORSO', 'Anno scorso'], ['PERSONALIZZATO', 'Dal… al…'],
];

/** Numero di filtri attivi, esclusi ricerca e ordinamento. */
export const contaFiltriAttivi = (f: FiltriClienti): number =>
    (f.tipo !== 'TUTTI' ? 1 : 0) + (f.ambito !== 'TUTTI' ? 1 : 0) + (f.tipiMandato.length > 0 ? 1 : 0)
    + (f.periodo !== 'TUTTO' ? 1 : 0) + (f.stato !== 'TUTTI' ? 1 : 0);

const mese = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Intervallo di mesi [dal, al] del periodo scelto; null = nessun limite. */
export const intervalloPeriodo = (f: FiltriClienti, oggi: Date = new Date()): [string, string] | null => {
    const anno = oggi.getFullYear();
    switch (f.periodo) {
        case 'MESE': return [mese(oggi), mese(oggi)];
        case 'TRE_MESI': return [mese(new Date(anno, oggi.getMonth() - 2, 1)), mese(oggi)];
        case 'ANNO': return [`${anno}-01`, `${anno}-12`];
        case 'ANNO_SCORSO': return [`${anno - 1}-01`, `${anno - 1}-12`];
        case 'PERSONALIZZATO': {
            if (!f.dal && !f.al) return null;
            const [a, b] = [f.dal || '0000-01', f.al || '9999-12'];
            return a <= b ? [a, b] : [b, a];
        }
        default: return null;
    }
};

// Minuscole e senza accenti, per una ricerca che non dipenda da come è stato scritto il nome.
const normalizza = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const testoRicerca = (c: Client): string => {
    const x = c as any;
    return normalizza([
        getClientDisplayName(c), getClientReferente(c), getClientEmail(c), getClientTelefono(c),
        getClientPartitaIva(c), getClientPec(c), x.codiceFiscale, x.email, x.telefono, x.citta,
    ].filter(Boolean).join(' | '));
};

export const ORDINI: [OrdineClienti, string][] = [
    ['NOME', 'Ordine alfabetico (nome)'], ['COGNOME', 'Ordine alfabetico (cognome)'], ['RECENTI', 'Attività più recente'],
];

/**
 * Chiave di ordinamento: il nome come appare nella lista (ragione sociale o "Nome Cognome");
 * con `perCognome` i privati vanno per cognome e nome.
 */
export const chiaveOrdine = (c: Client, perCognome = false): string => {
    if (isClienteAzienda(c) || !perCognome) return getClientDisplayName(c);
    const x = c as any;
    return `${x.surname || ''} ${x.nome || ''}`.trim() || getClientDisplayName(c);
};

const confronta = (a: string, b: string) => a.localeCompare(b, 'it', { sensitivity: 'base', numeric: true });

const totaleMandati = (a?: ClienteAttivita) =>
    a ? a.mandatiInvestigativi + a.mandatiBonifica + a.mandatiConsulenza : 0;

/** Applica ricerca, filtri e ordinamento. `attivita` è indicizzato per id cliente. */
export function filtraClienti<C extends Client>(
    clienti: C[], attivita: Record<string, ClienteAttivita>, f: FiltriClienti, oggi: Date = new Date(),
): C[] {
    const termini = normalizza(f.cerca.trim()).split(/\s+/).filter(Boolean);
    const intervallo = intervalloPeriodo(f, oggi);

    const risultato = clienti.filter(c => {
        const a = attivita[String(c.id)];
        const mandati = totaleMandati(a);
        const servizi = a?.servizi || 0;

        if (f.tipo === 'AZIENDE' && !isClienteAzienda(c)) return false;
        if (f.tipo === 'PRIVATI' && isClienteAzienda(c)) return false;

        if (f.ambito === 'INVESTIGATIVI' && mandati === 0) return false;
        if (f.ambito === 'FIDUCIARI' && servizi === 0) return false;

        if (f.tipiMandato.length > 0) {
            const ok = (f.tipiMandato.includes('INVESTIGATIVO') && (a?.mandatiInvestigativi || 0) > 0)
                || (f.tipiMandato.includes('BONIFICA') && (a?.mandatiBonifica || 0) > 0)
                || (f.tipiMandato.includes('CONSULENZA') && (a?.mandatiConsulenza || 0) > 0);
            if (!ok) return false;
        }

        if (intervallo) {
            const [dal, al] = intervallo;
            if (!(a?.mesi || []).some(m => m >= dal && m <= al)) return false;
        }

        if (f.stato === 'APERTI' && !a?.lavoriAperti) return false;
        if (f.stato === 'CONCLUSI' && (mandati + servizi === 0 || a?.lavoriAperti)) return false;
        if (f.stato === 'SENZA' && mandati + servizi > 0) return false;

        if (termini.length > 0) {
            const testo = testoRicerca(c);
            // Il telefono si trova anche scritto senza spazi.
            const testoCompatto = testo.replace(/[\s.\-/]/g, '');
            if (!termini.every(t => testo.includes(t) || testoCompatto.includes(t.replace(/[\s.\-/]/g, '')))) return false;
        }
        return true;
    });

    return risultato.sort((x, y) => {
        if (f.ordine === 'RECENTI') {
            const ux = attivita[String(x.id)]?.ultimaAttivita || '';
            const uy = attivita[String(y.id)]?.ultimaAttivita || '';
            if (ux !== uy) return ux < uy ? 1 : -1;
        }
        const perCognome = f.ordine === 'COGNOME';
        return confronta(chiaveOrdine(x, perCognome), chiaveOrdine(y, perCognome));
    });
}
